




























































































import { Component, Prop, Vue } from 'vue-property-decorator'
import { TypeformFormAPI, TypeformResponseAnswerHookAPI } from '@/api/backend-client'
import { TypeformForm } from '@/api/model/typeform/typeform-form'
import { TypeformFieldUnion, TypeformFieldArrayUnion, TypeformField } from '@/api/model/typeform/typeform-field'
import { TypeformWelcomeScreen } from '@/api/model/typeform/typeform-welcome-screen'
import { TypeformThankYouScreen } from '@/api/model/typeform/typeform-thank-you-screen'
import { TypeformLogicJump } from '@/api/model/typeform/typeform-logic-jump'
import { TypeformReferenceRegExp } from '@/api/model/typeform/typeform-form'
import Fieldset from '@/views/Company/Settings/Typeform/Forms/Detail/Fieldset.vue'
import PopOut from '@/views/Company/Settings/Typeform/Forms/Detail/PopOut.vue'
import { v4 as uuid } from 'uuid'
import { TypeformResponseAnswerHook } from '@/api/model/typeform/typeform-response-answer-hook'

@Component({
    components: {
        TypeformFieldset: Fieldset,
        TypeformPopOut: PopOut,
    },
})
export default class CompanySettingsTypeformFormsDetail extends Vue {
    @Prop({ required: true }) readonly formId!: string
    form: TypeformForm = null
    hooks: TypeformResponseAnswerHook[] = []
    loading: boolean = false
    saving: boolean = false
    addingField: TypeformFieldUnion = null
    adding: boolean = false

    async created(): Promise<void> {
        await this.reload()
    }

    async reload(): Promise<void> {
        this.loading = true
        this.form = await TypeformFormAPI.get(this.formId)
        this.hooks = await TypeformResponseAnswerHookAPI.getAll({
            formId: this.formId,
        })
        this.loading = false
    }

    async save(): Promise<void> {
        this.saving = true
        try {
            this.form = await TypeformFormAPI.update(this.formId, this.form)
        } catch (error) {
            this.$notify({
                group: 'validation',
                type: 'error',
                title: this.$t('error.title').toString(),
                text: this.$t('error.text', { message: error.message }).toString().replace(/\n/g, '<br>'),
            })
        } finally {
            this.saving = false
        }
    }

    async goToList(): Promise<void> {
        await this.$router.push({
            name: 'company_settings_typeform_forms_list',
        })
    }

    get thankYouScreens(): TypeformThankYouScreen[] {
        return this.form.thankyou_screens.filter(t => !t.isDefault)
    }

    set thankYouScreens(thankyou_screens: TypeformThankYouScreen[]) {
        this.form.thankyou_screens = thankyou_screens
    }

    onAddWelcomeScreen(): void {
        this.onAdd(TypeformWelcomeScreen.fromData({
            ref: uuid(),
        }))
    }

    onAddField(): void {
        this.onAdd(TypeformField.fromData({
            ref: uuid(),
        }))
    }

    onAddThankYouScreen(): void {
        this.onAdd(TypeformThankYouScreen.fromData({
            ref: uuid(),
        }))
    }

    onAdd(field: TypeformFieldUnion): void {
        this.addingField = field
        this.adding = true
    }

    onDeleteWelcomeScreen(field: TypeformWelcomeScreen): void {
        this.form.welcome_screens = this.onDelete(
            field,
            'welcome_screens',
            this.form.welcome_screens,
            [].concat(this.form.fields, this.form.thankyou_screens),
        ) as TypeformWelcomeScreen[]
    }

    onDeleteField(field: TypeformField): void {
        this.form.fields = this.onDelete(
            field,
            'fields',
            this.form.fields,
            [].concat(this.form.welcome_screens, this.form.thankyou_screens),
        ) as TypeformField[]
    }

    onDeleteThankYouScreen(field: TypeformThankYouScreen): void {
        this.form.thankyou_screens = this.onDelete(
            field,
            'thankyou_screens',
            this.form.thankyou_screens,
            [].concat(this.form.welcome_screens, this.form.fields),
        ) as TypeformThankYouScreen[]
    }

    onDelete(field: TypeformFieldUnion, fieldsType: string, fields: TypeformFieldArrayUnion, otherFields: TypeformFieldUnion[]): TypeformFieldArrayUnion {
        const filteredFields = fields.filter(internalField => internalField.ref !== field.ref)
        const from = [].concat(filteredFields, otherFields)
        const to = [field]

        const hasInvalidReferences = this.hasReferences(from, to)
        const hasInvalidLogicJumpActions = this.hasLogicJumpActions(from, to, true)

        let confirmMessageKey = null
        if (hasInvalidReferences && hasInvalidLogicJumpActions) {
            confirmMessageKey = 'references_logic_jump_actions'
        } else if (hasInvalidReferences) {
            confirmMessageKey = 'references'
        } else if (hasInvalidLogicJumpActions) {
            confirmMessageKey = 'logic_jump_actions'
        }

        let confirmMessage = ''
        if (confirmMessageKey) {
            confirmMessage += this.$t(`company_settings.typeform_forms.${fieldsType}.delete_confirm.${confirmMessageKey}`)
            confirmMessage += '\n\n'
        }
        confirmMessage += this.$t(`company_settings.typeform_forms.${fieldsType}.delete_confirm.confirm`)

        if (confirm(confirmMessage)) {
            this.removeReferences(from, to)
            this.removeLogicJumpActions(from, to, true)

            return filteredFields
        }

        return fields
    }

    onMoveWelcomeScreen(field: TypeformWelcomeScreen, oldIndex: number, newIndex: number): void {
        this.form.welcome_screens = this.onMove(
            field,
            oldIndex,
            newIndex,
            'welcome_screens',
            this.form.welcome_screens,
        ) as TypeformWelcomeScreen[]
    }

    onMoveField(field: TypeformField, oldIndex: number, newIndex: number): void {
        this.form.fields = this.onMove(
            field,
            oldIndex,
            newIndex,
            'fields',
            this.form.fields,
        ) as TypeformField[]
    }

    onMoveThankYouScreen(field: TypeformThankYouScreen, oldIndex: number, newIndex: number): void {
        this.form.thankyou_screens = this.onMove(
            field,
            oldIndex,
            newIndex,
            'thankyou_screens',
            this.form.thankyou_screens,
        ) as TypeformThankYouScreen[]
    }

    onMove(field: TypeformFieldUnion, oldIndex: number, newIndex: number, fieldsType: string, fields: TypeformFieldArrayUnion): TypeformFieldArrayUnion {
        let from = []
        let to = []

        if (newIndex < oldIndex) {
            // Field moved before other fields, the moved field should no longer reference the other fields
            from = [field]
            to = fields.slice(newIndex + 1, oldIndex + 1)
        } else if (newIndex > oldIndex) {
            // Field moved after other fields, the other fields should no longer reference the moved field
            from = fields.slice(oldIndex, newIndex)
            to = [field]
        } else {
            return fields
        }

        const hasInvalidReferences = this.hasReferences(from, to)
        const hasInvalidLogicJumpActions = this.hasLogicJumpActions(from, to)

        let confirmMessageKey = null
        if (hasInvalidReferences && hasInvalidLogicJumpActions) {
            confirmMessageKey = 'references_logic_jump_actions'
        } else if (hasInvalidReferences) {
            confirmMessageKey = 'references'
        } else if (hasInvalidLogicJumpActions) {
            confirmMessageKey = 'logic_jump_actions'
        } else {
            return fields
        }

        let confirmMessage = ''
        confirmMessage += this.$t(`company_settings.typeform_forms.${fieldsType}.move_confirm.${confirmMessageKey}`)
        confirmMessage += '\n\n'
        confirmMessage += this.$t(`company_settings.typeform_forms.${fieldsType}.move_confirm.confirm`)

        if (confirm(confirmMessage)) {
            this.removeReferences(from, to)
            this.removeLogicJumpActions(from, to)
        } else {
            if (newIndex < oldIndex) {
                // Field moved before other fields, field should be moved back after these other fields
                fields = [].concat(
                    fields.slice(0, newIndex),                // All fields until the field
                    fields.slice(newIndex + 1, oldIndex + 1), // All fields between the new position and the old position of the field
                    [field],                                  // The field
                    fields.slice(oldIndex + 1),               // All fields after the old position of the field
                )
            } else if (newIndex > oldIndex) {
                // Field moved after other fields, field should be moved back before these other fields
                fields = [].concat(
                    fields.slice(0, oldIndex),        // All fields until the field
                    [field],                          // The field
                    fields.slice(oldIndex, newIndex), // All fields between the old position and the new position of the field
                    fields.slice(newIndex + 1),       // All fields after the new position of the field
                )
            }
        }

        return fields
    }

    hasReferences(from: TypeformFieldArrayUnion, to: TypeformFieldArrayUnion): boolean {
        for (const fromField of from) {
            const properties = ['title', 'description']
            for (const property of properties) {
                const value = fromField[property]
                if (!value) {
                    continue
                }

                const valueParts = value.split(TypeformReferenceRegExp)
                while (valueParts.length) {
                    const valuePart = valueParts.shift()
                    if (!valuePart.match(TypeformReferenceRegExp)) {
                        continue
                    }

                    const referenceType = valueParts.shift()
                    const reference = valueParts.shift()

                    if (referenceType === 'field') {
                        if (to.find(toField => toField.ref === reference)) {
                            return true
                        }
                    }
                }
            }
        }

        return false
    }

    removeReferences(from: TypeformFieldArrayUnion, to: TypeformFieldArrayUnion): void {
        for (const fromField of from) {
            const properties = ['title', 'description']
            for (const property of properties) {
                const value = fromField[property]
                if (!value) {
                    continue
                }

                let newValue = ''

                const valueParts = value.split(TypeformReferenceRegExp)
                while (valueParts.length) {
                    const valuePart = valueParts.shift()
                    if (!valuePart.match(TypeformReferenceRegExp)) {
                        newValue += valuePart
                        continue
                    }

                    const referenceType = valueParts.shift()
                    const reference = valueParts.shift()

                    if (referenceType === 'field') {
                        if (to.find(toField => toField.ref === reference)) {
                            continue
                        }
                    }

                    newValue += valuePart
                }

                fromField[property] = newValue
            }
        }
    }

    hasLogicJumpActions(from: TypeformFieldArrayUnion, to: TypeformFieldArrayUnion, includeActionTo: boolean = false): boolean {
        for (const fromField of from) {
            const logicJump = this.form.getLogicForField(fromField)
            if (!logicJump) {
                continue
            }

            for (const action of logicJump.actions) {
                if (includeActionTo) {
                    if (action.details.to.type === 'field' || action.details.to.type === 'thankyou') {
                        if (to.find(toField => toField.ref === action.details.to.value)) {
                            return true
                        }
                    }
                }

                const conditions = action.flattenedConditions
                for (const condition of conditions) {
                    if (condition.vars.length < 1) {
                        continue
                    }

                    if (condition.vars[0].type === 'field') {
                        if (to.find(toField => toField.ref === condition.vars[0].value)) {
                            return true
                        }
                    }
                }
            }
        }

        return false
    }

    removeLogicJumpActions(from: TypeformFieldArrayUnion, to: TypeformFieldArrayUnion, includeActionTo: boolean = false): void {
        for (const fromField of from) {
            const logicJump = this.form.getLogicForField(fromField)
            if (!logicJump) {
                continue
            }

            logicJump.actions = logicJump.actions.filter(action => {
                if (includeActionTo) {
                    if (action.details.to.type === 'field' || action.details.to.type === 'thankyou') {
                        if (to.find(toField => toField.ref === action.details.to.value)) {
                            return false
                        }
                    }
                }

                const conditions = action.flattenedConditions
                for (const condition of conditions) {
                    if (condition.vars.length < 1) {
                        continue
                    }

                    if (condition.vars[0].type === 'field') {
                        if (to.find(toField => toField.ref === condition.vars[0].value)) {
                            return false
                        }
                    }
                }

                return true
            })

            this.onUpdateLogicJump(logicJump)
        }
    }

    onUpdateLogicJump(logicJump: TypeformLogicJump): void {
        const logicJumpIndex = this.form.logic.findIndex(existingLogicJump => existingLogicJump.type === logicJump.type && existingLogicJump.ref === logicJump.ref)
        if (logicJumpIndex !== -1) {
            if (logicJump.actions.length > 0) {
                // Edit existing logic jump
                this.form.logic.splice(logicJumpIndex, 1, logicJump)
            } else {
                // Remove existing logic jump
                this.form.logic.splice(logicJumpIndex, 1)
            }
        } else {
            if (logicJump.actions.length > 0) {
                // Add logic jump
                this.form.logic.push(logicJump)
            }
        }
    }

    async onUpdateHooks(hooks: TypeformResponseAnswerHook[]): Promise<void> {
        for (const hook of hooks) {
            const result = await TypeformResponseAnswerHookAPI.update(hook.id, hook)

            if (!result) {
                await TypeformResponseAnswerHookAPI.create(hook)
            }
        }

        await this.reload()
    }

    async onDeleteHooks(hooks: TypeformResponseAnswerHook[]): Promise<void> {
        for (const hook of hooks) {
            await TypeformResponseAnswerHookAPI.delete(hook.id)
        }

        await this.reload()
    }

    onPopOutSave(field: TypeformFieldUnion): void {
        if (field instanceof TypeformField) {
            this.form.fields.push(field)
        }

        if (field instanceof TypeformWelcomeScreen) {
            this.form.welcome_screens.push(field)
        }

        if (field instanceof TypeformThankYouScreen) {
            this.form.thankyou_screens.push(field)
        }
    }

    onPopOutClosed(): void {
        this.addingField = null
        this.adding = false
    }
}
